// Deterministic anaemia auto-classifier (BSH-aligned) — reads Hb, MCV, RDW,
// reticulocytes, RBC (Mentzer), ferritin, B12, folate from entered/last labs
// and suggests the LIKELY anaemia type + next workup. Aid only, never a
// diagnosis: clinical correlation always required.

export function classifyAnaemia(v) {
  const { hb, mcv, rdw, retic, rbc, ferritin, b12, folate, female } = v;
  if (hb == null) return null;
  const cutoff = female ? 12 : 13;
  if (hb >= cutoff) return null;
  const severity = hb < 7 ? "severe" : hb < 10 ? "moderate" : "mild";
  const rdwHigh = rdw != null && rdw > 14.5;
  const reticHigh = retic != null && retic > 2.5;
  let type, workup, guideline = "BSH-IDA 2021";

  if (mcv != null && mcv < 80) {
    const mentzer = rbc ? mcv / rbc : null;
    if (ferritin != null && ferritin < 30) {
      type = "Iron-deficiency anaemia (microcytic, ferritin low)";
      workup = ["Find the source — menstrual/GI loss; low threshold for endoscopy >50 y", "Alternate-day oral iron; recheck Hb in 2–4 weeks"];
    } else if (mentzer != null && mentzer < 13 && !rdwHigh) {
      type = "Possible thalassaemia trait (microcytic, Mentzer < 13)";
      workup = ["HPLC / Hb electrophoresis", "Avoid empirical iron unless ferritin is low"];
    } else if (rdwHigh) {
      type = "Microcytic — iron deficiency likely (RDW high)";
      workup = ["Ferritin + transferrin saturation to confirm", "Screen for blood loss"];
    } else {
      type = "Microcytic anaemia — iron studies needed";
      workup = ["Ferritin + TSAT; thalassaemia screen if ferritin normal"];
    }
  } else if (mcv != null && mcv > 100) {
    guideline = "BSH-B12 2014";
    if (b12 != null && b12 < 200) {
      type = "Macrocytic — B12 deficiency";
      workup = ["Start B12 replacement; intrinsic-factor antibody", "Check folate alongside"];
    } else if (folate != null && folate < 4) {
      type = "Macrocytic — folate deficiency";
      workup = ["Folic acid 5 mg OD — confirm B12 normal first (risk of SACD)"];
    } else if (reticHigh) {
      type = "Macrocytic with reticulocytosis — haemolysis / recent bleed?";
      guideline = "ASH/BSH";
      workup = ["LDH, haptoglobin, indirect bilirubin, DCT, peripheral smear"];
    } else {
      type = "Macrocytic anaemia — cause not yet clear";
      workup = ["B12 + folate, TSH, LFT/GGT (alcohol), drug review (metformin, PPI)"];
    }
  } else if (mcv != null) {
    if (reticHigh) {
      type = "Normocytic with reticulocytosis — blood loss or haemolysis";
      guideline = "ASH/BSH";
      workup = ["LDH, haptoglobin, bilirubin, DCT; look for a bleeding source"];
    } else if (ferritin != null && ferritin < 30) {
      type = "Normocytic — early iron deficiency (ferritin low)";
      workup = ["Treat as IDA; screen for blood loss"];
    } else {
      type = "Normocytic anaemia — chronic disease / renal / early iron deficiency";
      workup = ["Ferritin + CRP, creatinine/eGFR", "Reticulocyte count if not done"];
    }
  } else {
    type = "Anaemia — MCV needed to classify";
    workup = ["Add MCV / RDW / retic — one tap via the Anaemia Workup package"];
  }

  const bits = [`Hb ${hb} (${severity})`];
  if (mcv != null) bits.push(`MCV ${mcv}`);
  if (rdw != null) bits.push(`RDW ${rdw}${rdwHigh ? " ↑" : ""}`);
  if (retic != null) bits.push(`retic ${retic}%${reticHigh ? " ↑" : ""}`);
  if (rbc != null && mcv != null && mcv < 80) bits.push(`Mentzer ${(mcv / rbc).toFixed(1)}`);
  if (ferritin != null) bits.push(`ferritin ${ferritin}${ferritin < 30 ? " ↓" : ""}`);
  if (b12 != null) bits.push(`B12 ${b12}${b12 < 200 ? " ↓" : ""}`);
  if (folate != null) bits.push(`folate ${folate}${folate < 4 ? " ↓" : ""}`);
  return { type, detail: bits.join(" · "), workup, guideline, severity };
}
